import { type FC, useState } from "react";

import { cn } from "@/lib/utils";

type Props = {
  name: string;
  /** Provider logo URL; falls back to initials when missing or broken. */
  src: string | null;
  className?: string;
};

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

/** The app's logo, plain in a 32px slot; initials on a fill when it cannot load. */
export const ConnectorLogo: FC<Props> = ({ name, src, className }) => {
  const [broken, setBroken] = useState(false);
  const showImage = Boolean(src) && !broken;
  return (
    <div
      className={cn(
        "flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg",
        !showImage && "bg-fill-2",
        className
      )}
      data-testid="connector-logo"
      data-fallback={showImage ? undefined : "true"}
    >
      {showImage ? (
        <img
          src={src ?? undefined}
          alt=""
          aria-hidden
          className="size-5 object-contain"
          loading="lazy"
          onError={() => setBroken(true)}
        />
      ) : (
        <span className="text-sm font-medium text-fg-3">
          {initialsOf(name)}
        </span>
      )}
    </div>
  );
};
